import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { recordingsApi, type AdminRecordingRow } from '../api/recordings'
import { adminApi } from '../api/settings'
import { DropdownSelect } from '../components/ui/DropdownSelect'
import { Pagination } from '../components/ui/Pagination'
import { RecordingPlayer } from '../components/RecordingPlayer'
import { PageSpinner } from '../components/ui/Spinner'
import { usePersistedPageSize } from '../hooks/usePersistedPageSize'
import { usePageTitle } from '../hooks/usePageTitle'
import { formatDateTime, formatDuration } from '../lib/utils'

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'matched', label: 'Matched' },
  { value: 'ambiguous', label: 'Ambiguous' },
  { value: 'unmatched', label: 'Unmatched' },
]

const STATUS_BADGE: Record<string, string> = {
  matched: 'badge badge-green',
  ambiguous: 'badge badge-yellow',
  unmatched: 'badge badge-gray',
}

export function RecordingsPage() {
  usePageTitle('Recordings')
  const { companyId: routeCompanyId } = useParams<{ companyId: string }>()
  const isAdmin = !routeCompanyId

  // Admin (no company in the URL) chooses a company; a company owner uses the route's company.
  const { data: companies } = useQuery({
    queryKey: ['admin-companies'],
    queryFn: () => adminApi.listCompanies().then(r => r.data as { id: string; name: string }[]),
    enabled: isAdmin,
  })
  const [selectedCompany, setSelectedCompany] = useState('')
  const companyId = routeCompanyId ?? selectedCompany

  const [page, setPage] = useState(1)
  const [limit, setLimit] = usePersistedPageSize('recordings', companyId)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['recordings', companyId, page, limit, search, status],
    queryFn: () =>
      recordingsApi
        .listAdmin(companyId, { page, limit, search: search || undefined, status: status || undefined })
        .then(r => r.data),
    enabled: !!companyId,
  })

  const rows = data?.data ?? []

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-gray-200 bg-white">
        <h1 className="page-title mb-3">Call Recordings</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <div className="w-64">
              <DropdownSelect
                value={selectedCompany}
                onChange={v => { setSelectedCompany(v); setPage(1) }}
                placeholder="Select a company…"
                searchable
                options={(companies ?? []).map(c => ({ value: c.id, label: c.name }))}
              />
            </div>
          )}
          <div className="w-64">
            <input
              className="input"
              placeholder="Search number or lead…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
            />
          </div>
          {isAdmin && (
            <div className="w-40">
              <DropdownSelect value={status} onChange={v => { setStatus(v); setPage(1) }} options={STATUS_OPTIONS} />
            </div>
          )}
        </div>
      </div>

      {isFetching && !isLoading && <div className="h-0.5 bg-blue-400 animate-pulse" />}

      <div className="flex-1 overflow-y-auto">
        {!companyId ? (
          <p className="text-sm text-gray-500 text-center py-12">Select a company to view its recordings.</p>
        ) : isLoading ? (
          <PageSpinner />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Agent</th>
                <th>Number</th>
                <th>Lead</th>
                <th>Duration</th>
                {isAdmin && <th>Status</th>}
                <th>Recording</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-16 text-gray-500">No recordings found</td></tr>
              ) : rows.map((r: AdminRecordingRow) => (
                <tr key={r.recordingId}>
                  <td className="text-gray-600 whitespace-nowrap">{formatDateTime(r.callDate)}</td>
                  <td className="text-gray-600">{r.agent ?? '—'}</td>
                  <td className="font-medium text-gray-900">{r.number ?? '—'}</td>
                  <td>
                    {r.leadId
                      ? <Link to={`/company/${companyId}/leads/${r.leadId}`} className="text-blue-600 hover:underline">{r.name || 'Lead'}</Link>
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="text-gray-600">{formatDuration(r.durationSec)}</td>
                  {isAdmin && <td><span className={STATUS_BADGE[r.matchStatus] ?? 'badge badge-gray'}>{r.matchStatus}</span></td>}
                  <td><RecordingPlayer companyId={companyId} recordingId={r.recordingId} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.meta.total > 0 && (
        <Pagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={limit}
          onPageChange={setPage}
          onPageSizeChange={s => { setLimit(s); setPage(1) }}
        />
      )}
    </div>
  )
}
